import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        scheduled: "border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100/80",
        fail: "border-transparent bg-red-100 text-red-700 hover:bg-red-100/80",
        warn: "border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100/80",
        active: "border-transparent bg-green-100 text-green-700 hover:bg-green-100/80",
        concluded: "border-transparent bg-gray-100 text-gray-700 hover:bg-gray-100/80",
        outcome_positive: "border-transparent bg-[#d2c8ff] text-[#3b2e70]",
        violet: "border-transparent bg-[hsl(var(--chart-brand-violet))] text-[#3b2e70]",
        yellow: "border-transparent bg-[hsl(var(--chart-brand-yellow))] text-[#1e1e1e]",
        outcome_negative: "border-transparent bg-[#bfbda7] text-[#4f4c39]",
        outcome_neutral: "border-transparent bg-[#e0e4ef] text-[#3e495f]",
        outcome_inconclusive: "border-transparent bg-[#f6f4f2] text-[#5d5650]",
        rollout_rolled_out: "border-transparent bg-emerald-100 text-emerald-800",
        rollout_follow_up: "border-transparent bg-sky-100 text-sky-800",
        rollout_learning: "border-transparent bg-amber-100 text-amber-800",
        durability_still_live: "border-transparent bg-emerald-100 text-emerald-800",
        // A "Still live" confirmation decays: these two are the same outcome seen through
        // an older check, not different outcomes. Neither may reuse durability_reverted's
        // red — a stale confirmation sitting in the same column as a real revert must not
        // read as one — so the scale runs emerald to amber to orange.
        durability_still_live_aging: "border-transparent bg-amber-100 text-amber-800",
        durability_still_live_stale: "border-orange-400 bg-orange-200 text-orange-900",
        durability_reverted: "border-transparent bg-red-100 text-red-800",
        durability_churned: "border-transparent bg-slate-200 text-slate-700",
        // Unconfirmed is an outline, not a fill: it has to read as an absence of
        // evidence next to the solid badges, never as a recorded outcome. It carries the
        // stale colour because no confirmation at all cannot rank below an old one.
        durability_unconfirmed: "border-dashed border-orange-400 text-orange-800",
        test: "border-transparent bg-test text-test-foreground hover:bg-test/80",
        control: "border-transparent bg-control text-control-foreground hover:bg-control/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
